import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Select, SidePanel, type BadgeVariant } from '@silverfalls/sterling';
import { FilterInput } from '../components/FilterInput.js';
import { useToast } from '../components/Toast.js';
import { formatRelativeDate } from '../lib/format.js';
import { sortBy, toggleSort, type SortState } from '../lib/sort.js';
import {
  createVendor,
  fetchVendors,
  type CreateVendorInput,
  type Form1099FormType,
  type Form1099Kind,
  type PaymentMethod,
  type PaymentTerms,
  type TaxExemptionKind,
  type VendorRow,
  type VendorState,
} from '../api/p2p.js';

const VENDOR_STATE_FILTERS: ReadonlyArray<{
  label: string;
  value: VendorState | 'ALL' | 'ACTIVE_ONLY';
}> = [
  // Default hides RETIRED + MERGED — they're terminal and usually noise on
  // a working list. Switch to "All" to see them.
  { label: 'Active only', value: 'ACTIVE_ONLY' },
  { label: 'All states', value: 'ALL' },
  { label: 'Prospect', value: 'PROSPECT' },
  { label: 'Onboarding', value: 'ONBOARDING' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Suspended', value: 'SUSPENDED' },
  { label: 'Retired', value: 'RETIRED' },
  { label: 'Merged', value: 'MERGED' },
];

const ACTIVE_VENDOR_STATES: ReadonlySet<VendorState> = new Set<VendorState>([
  'PROSPECT',
  'ONBOARDING',
  'ACTIVE',
  'SUSPENDED',
]);

// Sterling density: a column earns its place if its values vary. The
// vendor list defaults to four columns — name, state, created, location.
// Terms / method / GL / 1099 status are usually identical across most
// vendors at default density; they live on the detail page and (eventually)
// behind a column-picker for power users.
type VendorSortKey = 'legalName' | 'state' | 'createdAt' | 'location';

function vendorSortAccessor(v: VendorRow, key: VendorSortKey): unknown {
  switch (key) {
    case 'legalName':
      return v.legalName;
    case 'state':
      return v.state;
    case 'createdAt':
      return v.createdAt;
    case 'location':
      return `${v.city},${v.region},${v.country}`;
  }
}

function matchesQuery(v: VendorRow, q: string): boolean {
  if (q === '') return true;
  const needle = q.toLowerCase();
  return (
    v.legalName.toLowerCase().includes(needle) ||
    v.displayName.toLowerCase().includes(needle) ||
    v.vendorRef.toLowerCase().includes(needle) ||
    v.city.toLowerCase().includes(needle)
  );
}

// Requesting user is still hardcoded until auth lands.
const REQUESTING_USER = 'u-ui-skeleton';

const PAYMENT_TERMS: ReadonlyArray<PaymentTerms> = [
  'NET_15',
  'NET_30',
  'NET_45',
  'NET_60',
  'NET_90',
  'TWO_TEN_NET_30',
  'DUE_ON_RECEIPT',
];

const PAYMENT_METHODS: ReadonlyArray<PaymentMethod> = ['ACH', 'CHECK', 'WIRE', 'VIRTUAL_CARD'];

const FORM_1099_FORM_TYPES: ReadonlyArray<{ value: Form1099FormType; label: string }> = [
  { value: 'FORM_1099_MISC', label: '1099-MISC' },
  { value: 'FORM_1099_NEC', label: '1099-NEC' },
  { value: 'FORM_1099_INT', label: '1099-INT' },
  { value: 'FORM_1099_DIV', label: '1099-DIV' },
];

type DialogState = { open: false } | { open: true };

interface VendorsProps {
  readonly opcoRef: string;
}

export function Vendors({ opcoRef }: VendorsProps): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [vendors, setVendors] = useState<ReadonlyArray<VendorRow>>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ open: false });
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<VendorState | 'ALL' | 'ACTIVE_ONLY'>(
    'ACTIVE_ONLY',
  );
  const [sort, setSort] = useState<SortState<VendorSortKey> | null>({
    key: 'createdAt',
    direction: 'desc',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await fetchVendors(opcoRef);
      setVendors(rows);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [opcoRef]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    let filtered = vendors.filter((v) => matchesQuery(v, query));
    if (stateFilter === 'ACTIVE_ONLY') {
      filtered = filtered.filter((v) => ACTIVE_VENDOR_STATES.has(v.state));
    } else if (stateFilter !== 'ALL') {
      filtered = filtered.filter((v) => v.state === stateFilter);
    }
    return sortBy(filtered, sort, vendorSortAccessor);
  }, [vendors, query, stateFilter, sort]);

  const onSort = (key: VendorSortKey) => setSort((prev) => toggleSort(prev, key));

  const activeCount = vendors.filter((v) => v.state === 'ACTIVE').length;

  return (
    <section className="vendors-screen">
      <div className="screen-header">
        <div>
          <h2>Vendors</h2>
          <p className="screen-header-desc">
            {vendors.length} {vendors.length === 1 ? 'vendor' : 'vendors'}
            {activeCount > 0 && ` · ${activeCount} active`}
          </p>
        </div>
        <Button
          variant="primary"
          icon="plus"
          onClick={() => setDialog({ open: true })}
        >
          New vendor
        </Button>
      </div>

      {loadError && (
        <div className="alert alert-neg" role="alert">
          Couldn't load vendors: {loadError}
        </div>
      )}

      {loading ? (
        <div className="t-body-sm">Loading…</div>
      ) : vendors.length === 0 ? (
        <div className="empty-state">
          <p>No vendors in {opcoRef} yet. Create one to get started.</p>
        </div>
      ) : (
        <>
          <div className="list-toolbar">
            <FilterInput
              value={query}
              onChange={setQuery}
              placeholder="Search by name, ref, or city…"
              ariaLabel="Search vendors"
            />
            <Select
              value={stateFilter}
              onValueChange={(v) =>
                setStateFilter(v as VendorState | 'ALL' | 'ACTIVE_ONLY')
              }
              aria-label="State filter"
              options={VENDOR_STATE_FILTERS.map((f) => ({
                value: f.value,
                label: f.label,
              }))}
            />
            <span className="list-count t-body-sm">
              {visible.length} of {vendors.length}
            </span>
          </div>

          {visible.length === 0 ? (
            <div className="empty-state">
              <p>No vendors match "{query}".</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader sort={sort} columnKey="legalName" onSort={onSort}>
                    Legal name
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="state" onSort={onSort}>
                    State
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="createdAt" onSort={onSort}>
                    Created
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="location" onSort={onSort}>
                    Location
                  </SortHeader>
                </tr>
              </thead>
              <tbody>
                {visible.map((v) => (
                  <tr
                    key={v.vendorRef}
                    className="row-clickable"
                    onClick={() => navigate(`/vendors/${v.vendorRef}`)}
                  >
                    <td className="cell-primary">{v.legalName}</td>
                    <td>
                      <Badge variant={stateBadgeVariant(v.state)} dot={false}>
                        {v.state}
                      </Badge>
                    </td>
                    <td>{formatRelativeDate(v.createdAt)}</td>
                    <td>
                      {v.city}, {v.region}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {dialog.open && (
        <NewVendorDialog
          opcoRef={opcoRef}
          onClose={() => setDialog({ open: false })}
          onCreated={(vendorRef) => {
            setDialog({ open: false });
            toast.push(`Vendor created (${vendorRef})`, 'success');
            void load();
          }}
          onError={(message) => toast.push(message, 'error')}
        />
      )}
    </section>
  );
}

interface SortHeaderProps {
  readonly sort: SortState<VendorSortKey> | null;
  readonly columnKey: VendorSortKey;
  readonly onSort: (key: VendorSortKey) => void;
  readonly children: JSX.Element | string;
}

function SortHeader({ sort, columnKey, onSort, children }: SortHeaderProps): JSX.Element {
  const active = sort?.key === columnKey;
  const indicator = active ? (sort?.direction === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th>
      <button
        type="button"
        className={`th-sort${active ? ' th-sort-active' : ''}`}
        onClick={() => onSort(columnKey)}
      >
        {children}
        <span className="th-sort-indicator" aria-hidden>
          {indicator}
        </span>
      </button>
    </th>
  );
}

function stateBadgeVariant(state: VendorRow['state']): BadgeVariant {
  switch (state) {
    case 'ACTIVE':
      return 'success';
    case 'ONBOARDING':
    case 'PROSPECT':
      return 'info';
    case 'SUSPENDED':
      return 'warning';
    case 'RETIRED':
    case 'MERGED':
      return 'neutral';
  }
}


// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

interface NewVendorDialogProps {
  readonly opcoRef: string;
  readonly onClose: () => void;
  readonly onCreated: (vendorRef: string) => void;
  readonly onError: (message: string) => void;
}

function NewVendorDialog({ opcoRef, onClose, onCreated, onError }: NewVendorDialogProps): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form1099Kind, setForm1099Kind] = useState<Form1099Kind>('NOT_REQUIRED');
  const [taxKind, setTaxKind] = useState<TaxExemptionKind>('TAXABLE');
  // SidePanel renders its Save button in the header; we trigger native
  // form submit so HTML5 validation + FormData still apply without
  // refactoring every field to controlled state. The ref is on the
  // <form> below; Save calls form.requestSubmit().
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    const form = event.currentTarget;
    const data = new FormData(form);

    const form1099Status: CreateVendorInput['form1099Status'] =
      form1099Kind === 'REQUIRED'
        ? { kind: 'REQUIRED', formType: (data.get('form1099FormType') as Form1099FormType) ?? 'FORM_1099_MISC' }
        : { kind: 'NOT_REQUIRED' };
    const taxExemptionStatus: CreateVendorInput['taxExemptionStatus'] =
      taxKind === 'EXEMPT'
        ? { kind: 'EXEMPT', certificateRef: String(data.get('certificateRef') ?? '').trim() }
        : { kind: 'TAXABLE' };

    const input: CreateVendorInput = {
      legalName: String(data.get('legalName') ?? '').trim(),
      displayName: String(data.get('displayName') ?? '').trim(),
      taxId: String(data.get('taxId') ?? '').trim(),
      addressLine1: String(data.get('addressLine1') ?? '').trim(),
      city: String(data.get('city') ?? '').trim(),
      region: String(data.get('region') ?? '').trim(),
      postalCode: String(data.get('postalCode') ?? '').trim(),
      country: String(data.get('country') ?? '').trim().toUpperCase(),
      paymentTerms: (data.get('paymentTerms') as PaymentTerms) ?? 'NET_30',
      paymentMethod: (data.get('paymentMethod') as PaymentMethod) ?? 'ACH',
      defaultGLAccount: String(data.get('defaultGLAccount') ?? '').trim(),
      form1099Status,
      taxExemptionStatus,
      opcoRef,
      requestingUser: REQUESTING_USER,
    };

    try {
      const result = await createVendor(input);
      if (result.__typename === 'OnboardVendorFailure') {
        const msg = `${result.class}: ${JSON.stringify(result.details)}`;
        setError(msg);
        onError(msg);
        setSubmitting(false);
        return;
      }
      onCreated(result.vendorRef);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      onError(msg);
      setSubmitting(false);
    }
  };

  return (
    <SidePanel
      open
      title="New vendor"
      width={640}
      onClose={onClose}
      onSave={() => formRef.current?.requestSubmit()}
      dirty={!submitting}
      saveLabel={submitting ? 'Creating…' : 'Create vendor'}
    >
      <form ref={formRef} className="form-grid" onSubmit={onSubmit}>
          <Field label="Legal name" name="legalName" required defaultValue="" />
          <Field label="Display name" name="displayName" required defaultValue="" />
          <Field label="Tax ID" name="taxId" required defaultValue="" />
          <Field label="Address line 1" name="addressLine1" required defaultValue="" />
          <Field label="City" name="city" required defaultValue="" />
          <Field label="Region" name="region" required defaultValue="" placeholder="WA" />
          <Field label="Postal code" name="postalCode" required defaultValue="" />
          <Field label="Country (ISO-2)" name="country" required defaultValue="US" />
          <Field label="Default GL account" name="defaultGLAccount" required defaultValue="6100-MRO" />

          <label className="form-row">
            <span className="form-label">Payment terms</span>
            <select name="paymentTerms" defaultValue="NET_30" required>
              {PAYMENT_TERMS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>

          <label className="form-row">
            <span className="form-label">Payment method</span>
            <select name="paymentMethod" defaultValue="ACH" required>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>

          <label className="form-row">
            <span className="form-label">1099 status</span>
            <select
              value={form1099Kind}
              onChange={(e) => setForm1099Kind(e.target.value as Form1099Kind)}
              required
            >
              <option value="NOT_REQUIRED">Not required</option>
              <option value="REQUIRED">Required</option>
            </select>
          </label>

          {form1099Kind === 'REQUIRED' ? (
            <label className="form-row">
              <span className="form-label">1099 form type</span>
              <select name="form1099FormType" defaultValue="FORM_1099_MISC" required>
                {FORM_1099_FORM_TYPES.map((ft) => (
                  <option key={ft.value} value={ft.value}>
                    {ft.label}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div className="form-row form-row-spacer" aria-hidden />
          )}

          <label className="form-row">
            <span className="form-label">Tax exemption</span>
            <select
              value={taxKind}
              onChange={(e) => setTaxKind(e.target.value as TaxExemptionKind)}
              required
            >
              <option value="TAXABLE">Taxable</option>
              <option value="EXEMPT">Exempt</option>
            </select>
          </label>

          {taxKind === 'EXEMPT' ? (
            <Field
              label="Certificate ref"
              name="certificateRef"
              required
              defaultValue=""
              placeholder="exemption-cert-id"
            />
          ) : (
            <div className="form-row form-row-spacer" aria-hidden />
          )}

          {error && (
            <div className="alert alert-neg" role="alert">
              {error}
            </div>
          )}
      </form>
    </SidePanel>
  );
}

interface FieldProps {
  readonly label: string;
  readonly name: string;
  readonly required?: boolean;
  readonly defaultValue?: string;
  readonly placeholder?: string;
}

function Field({ label, name, required, defaultValue, placeholder }: FieldProps): JSX.Element {
  return (
    <label className="form-row">
      <span className="form-label">{label}</span>
      <input
        type="text"
        name={name}
        required={required}
        defaultValue={defaultValue}
        placeholder={placeholder}
      />
    </label>
  );
}
